import { useMemo } from 'react'
import jwtDecode from 'jwt-decode'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { IconColor } from '@/atoms/utils/IconTypes'
import { slugs } from '@/constants/slugs'
import { Offer } from '@/services/PifService'
import { useUser } from '@/services/UserService'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, TranslateFunction } from '@/utils/translate/translate-client'

export const addGuildUpsellToOffer = (offer: Offer | null): Offer | null => {
  if (!offer) return null

  return { ...offer, slug: `${offer?.slug}-guild` }
}

const GUILD_UPSELL_SLUGS: Set<string> = new Set([])

export const hasGuildUpsell = (slug: string) => {
  return GUILD_UPSELL_SLUGS.has(slug)
}

const EXCLUSIVE_SCENE_SLUGS: Set<string> = new Set([])

export const hasExclusiveScene = (slug: string) => {
  return EXCLUSIVE_SCENE_SLUGS.has(slug)
}

export type PerkType = 'PIF' | 'streaming-access' | 'guild' | 'exclusive-scene' | 'default'

export const getExtraPerksForTheatricalSlug = (
  slug: string,
  isGuildMember: boolean,
  guildMembershipIncluded: boolean,
  t: TranslateFunction,
) => {
  const extraPerks: { id: string; type: PerkType; text: string | JSX.Element; enabled: boolean }[] = []

  if (hasExclusiveScene(slug)) {
    extraPerks.push({
      id: `exclusive-scene-${slug}`,
      type: 'exclusive-scene',
      text: t('exclusiveNewScenePerkExpressV2', 'Exclusive new scene delivered by email.'),
      enabled: true,
    })
  }

  if (slug === slugs.truthAndTreason && !isGuildMember) {
    extraPerks.push({
      id: `truth-and-treason-access`,
      type: 'streaming-access',
      text: (
        <Translate
          t={t}
          i18nKey="accessToTruthAndTreasonLimitedSeriesFirstEpisodes"
          defaults="Enjoy instant access to the first two episodes of the Truth & Treason Limited Series"
        />
      ),
      enabled: true,
    })

    extraPerks.push({
      id: `truth-and-treason-guild-upsell`,
      type: 'guild',
      text: (
        <Translate
          t={t}
          i18nKey="accessToTruthAndTreasonLimitedSeriesAndEntireAngelLibrary"
          defaults="Access to the rest of the <bold>Truth & Treason Limited Series</bold> and the <bold>ENTIRE</bold> Angel library."
          components={{ bold: <b /> }}
        />
      ),
      enabled: guildMembershipIncluded,
    })
  }

  if (slug === slugs.david && !isGuildMember) {
    extraPerks.push({
      id: `young-david-guild-upsell`,
      type: 'guild',
      text: (
        <Translate
          t={t}
          i18nKey="accessToYoungDavidSeriesAndEntireAngelLibrary"
          defaults="Access to the rest of the <bold>Young David Series</bold> and the <bold>ENTIRE</bold> Angel library."
          components={{ bold: <b /> }}
        />
      ),
      enabled: guildMembershipIncluded,
    })
  }

  if (slug === slugs.truthAndTreason && isGuildMember) {
    extraPerks.push({
      id: `truth-and-treason-access`,
      type: 'streaming-access',
      text: (
        <Translate
          t={t}
          i18nKey="accessToTruthAndTreasonFullLimitedSeries"
          defaults="Enjoy instant access to the first two episodes of the <bold>Truth & Treason Limited Series</bold> and the rest of the episodes as they become available."
          components={{ bold: <b /> }}
        />
      ),
      enabled: true,
    })
  }

  if (slug === slugs.david && isGuildMember) {
    extraPerks.push({
      id: `young-david-access`,
      type: 'streaming-access',
      text: (
        <Translate
          t={t}
          i18nKey="accessToYoungDavidSeriesFullSeries"
          defaults="Enjoy instant access to all of the <bold>Young David Series</bold>."
          components={{ bold: <b /> }}
        />
      ),
      enabled: true,
    })
  }

  return extraPerks
}

export const getCustomPerkIcon = (type: PerkType, enabled: boolean, isDarkMode: boolean) => {
  if (type === 'PIF') {
    return (
      <PayItForwardOutlineIcon size={24} color={isDarkMode ? 'oxide-bright' : 'oxide-primary'} className="shrink-0" />
    )
  }

  if (type === 'streaming-access' || type === 'exclusive-scene') {
    return <PlaySquareIcon size={24} color={isDarkMode ? 'oxide-bright' : 'oxide-primary'} className="shrink-0" />
  }

  if (type === 'guild') {
    const [darkColor, lightColor] = (
      enabled ? ['oxide-bright', 'oxide-primary'] : ['core-gray-400', 'core-gray-400']
    ) as [IconColor, IconColor]

    return <GuildIconMono size={24} color={isDarkMode ? darkColor : lightColor} className="shrink-0" />
  }

  return null
}

export const getGuildUpsellLabels = (slug: string, t: TranslateFunction) => {
  if (slug === slugs.truthAndTreason) {
    return {
      pifLabel: t('unlockEpisodeOneAndTwo', 'Unlock Episode 1 & 2'),
      guildLabel: t('episodesThreeAndFour', 'Episodes 3 & 4'),
    }
  }

  if (slug === slugs.david) {
    return {
      pifLabel: t('unlockEpisodeOneTwoAndThree', 'Unlock Episode 1, 2, & 3'),
      guildLabel: t('episodesFourAndFive', 'Episodes 4 & 5'),
    }
  }

  return null
}

export const getGuildUpsellCheckboxText = (slug: string, t: TranslateFunction) => {
  if (slug === slugs.truthAndTreason) {
    return (
      <Translate
        t={t}
        i18nKey="addGuildMembershipToOrderAndGetFirstMonthFreeTruthAndTreason"
        defaults="Check this box to claim your <bold>FREE</bold> month of Angel Guild Premium Membership ($20 value). Your membership includes 2 tickets to every Angel theatrical release, instant access to all <bold>4 Episodes of the Truth & Treason limited series</bold> as they release."
        components={{ bold: <b /> }}
      />
    )
  }

  if (slug === slugs.david) {
    return (
      <Translate
        t={t}
        i18nKey="addGuildMembershipToOrderAndGetFirstMonthFreeYoungDavidSeries"
        defaults="Check this box to claim your <bold>FREE</bold> month of Angel Guild Premium Membership ($20 value). Your membership includes 2 tickets to every Angel theatrical release, and instant access to all <bold>5 Episodes of the Young David series</bold>."
        components={{ bold: <b /> }}
      />
    )
  }

  return (
    <Translate
      t={t}
      i18nKey="checkTheBoxToClaimYourFirstMonthFreeOfAngelGuildPremiumMembershipDefaultRenews"
      defaults="Check this box to claim your <bold>FREE</bold> month of Angel Guild Premium Membership [$20 value]. Your membership includes 2 tickets to every Angel Studios theatrical release, instant access to stream Angel's entire library, and much more. Renews at $20 / month"
      components={{ bold: <b /> }}
    />
  )
}

export const getPerksDisclaimerText = (slug: string, t: TranslateFunction) => {
  if (slug === slugs.truthAndTreason) {
    const d = new Date()
    const isEps1And2Released = d >= new Date(2025, 9, 18)
    const isEpisode3Released = d >= new Date(2025, 9, 25)
    const isEpisode4Released = d >= new Date(2025, 10, 1)

    if (!isEps1And2Released) {
      return t(
        'episodesOneAndTwoPerkDisclaimer',
        'Episodes 1 and 2 release on October 17th. Episodes 3 and 4 release on October 24th and 31st.',
      )
    }

    if (isEps1And2Released && !isEpisode3Released) {
      return t(
        'episodesThreeAndFourPerkDisclaimer',
        'Episodes 1 and 2 are available to watch now. Episodes 3 and 4 release on October 24th and 31st.',
      )
    }

    if (isEpisode3Released && !isEpisode4Released) {
      return t(
        'episodeFourPerkDisclaimer',
        'Episodes 1, 2, and 3 are available to watch now. Episode 4 release on October 31st.',
      )
    }

    return null
  }

  return null
}

export const getBonusAccessConfig = (slug: string) => {
  if (slug === slugs.truthAndTreason) {
    return {
      title: 'Truth & Treason Limited Series',
      watchPath: '/watch/truth-and-treason-series',
      logo: {
        path: 'v1752255532/studio-app/catalog/62c0b6ad-5071-4f53-9256-69edc660fcda',
        width: 135,
        height: 98,
        color: '000000',
      },
    }
  }

  if (slug === slugs.david) {
    return {
      title: 'Young David Series',
      watchPath: '/watch/young-david',
      logo: {
        path: 'v1760028856/studio-app/catalog/2bd8f454-69d4-4593-8e57-5f6209bef64d',
        width: 175,
        height: 45,
        color: '000000',
      },
    }
  }

  return null
}

const hasValidToken = (token: string | string[] | null | undefined) => {
  if (!token || typeof token !== 'string') return false

  try {
    const decoded = jwtDecode<{ exp?: number }>(token)
    if (!decoded || !decoded.exp) return false
    const currentTime = Math.floor(Date.now() / 1000)
    return decoded.exp > currentTime
  } catch (error) {
    return false
  }
}

export const useExpressCheckoutConfirmationOnboarding = () => {
  const router = useSafeRouter()
  const { user, loading: userLoading } = useUser()

  const { email, isGuestUser, purchasedGuildMembership } = useMemo(() => {
    const queryEmail = typeof router.query?.email === 'string' ? router.query.email : null
    const queryIsGuestUser =
      typeof router.query?.loginViaTokenParam === 'string'
        ? router.query.loginViaTokenParam.toLowerCase() === 'true'
        : false
    typeof router.query?.token === 'string' && hasValidToken(router.query.token) ? router.query.token : null
    const purchasedGuildMembership = router.query.guildMembershipIncluded === '1'

    return {
      email: queryEmail ?? user?.email ?? null,
      isGuestUser: queryIsGuestUser,
      purchasedGuildMembership,
    }
  }, [router.query, user?.email])

  return {
    email,
    purchasedGuildMembership,
    isGuest: isGuestUser,
    isLoggedIn: !!user,
    loading: userLoading || !router.isReady,
  }
}
